/**
 * Друкована форма «Накладна-вимога на відпуск (внутрішнє переміщення)
 * матеріалів» (за мотивами типової М-11, наказ Мінстату №193) — ч.669.
 * Організація, № і дата, склад-джерело/підрозділ-одержувач, таблиця
 * матеріалів зі складом ПО РЯДКАХ (0458), кількість, собівартість
 * (для проведеного), підписи «Відпустив/Отримав».
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { materialRequisitions } from '@/api/materialRequisitions';
import { orgs, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { departmentsApi } from '@/api/hr';
import PrintActions from '@/shared/components/PrintActions';
import { partyCodesText } from '@/shared/utils/partyCodes';

const fmtQty = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { maximumFractionDigits: 3 });
const fmtMoney = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function MaterialRequisitionPrintPage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const docQ = useQuery({
    queryKey: ['material-requisition', id],
    queryFn: () => materialRequisitions.get(id!),
    enabled: !!id,
  });
  const d = docQ.data;
  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const whQ = useQuery({
    queryKey: ['warehouses', d?.organization_id],
    queryFn: () => refs.warehouses(d!.organization_id),
    enabled: !!d?.organization_id,
  });
  const deptQ = useQuery({
    queryKey: ['departments', d?.organization_id],
    queryFn: () => departmentsApi.list({ organization_id: d!.organization_id, limit: 500 }),
    enabled: !!d?.organization_id,
  });
  const nomListQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const uomsQ = useQuery({ queryKey: ['uoms'], queryFn: refs.uoms });

  if (!d || !org) {
    return <div style={{ padding: '2rem' }}>Завантаження вимоги…</div>;
  }

  const whName = new Map((whQ.data ?? []).map((w) => [w.id, w.name]));
  const nomById = new Map((nomListQ.data?.items ?? []).map((n) => [n.id, n]));
  const uomById = new Map((uomsQ.data ?? []).map((u) => [u.id, u.short_name]));
  const dept = deptQ.data?.items.find((x) => x.id === d.department_id);
  // Склад рядка > «Склад джерело» шапки (0458). Колонка складу — лише
  // коли складів у рядках більше одного (як друк реалізації, ч.661).
  const effWh = (it: { warehouse_id?: string | null }) =>
    it.warehouse_id || d.source_warehouse_id || '';
  const multiWarehouse = new Set(d.items.map(effWh).filter(Boolean)).size > 1;
  const uomOf = (it: { nomenclature_id: string; uom_id?: string | null }) => {
    if (it.uom_id) return uomById.get(it.uom_id) ?? 'шт';
    const n = nomById.get(it.nomenclature_id);
    return (n?.base_uom_id && uomById.get(n.base_uom_id)) || 'шт';
  };
  const totalCost = d.items.reduce((s, it) => s + Number(it.total_cost ?? 0), 0);

  return (
    <div className="print-page" style={{
      maxWidth: 800, margin: '0 auto', padding: '1.5rem',
      fontFamily: "'Times New Roman', serif", fontSize: '10.5pt', background: 'var(--bg-panel)',
    }}>
      <style>{`
        .mr-table { width: 100%; border-collapse: collapse; margin: 0.5rem 0; font-size: 9pt; line-height: 1.15; }
        .mr-table th, .mr-table td { border: 1px solid #000; padding: 1px 4px; }
        .mr-table th { text-align: center; font-weight: 600; font-size: 8pt; background: #f5f5f5; text-transform: none; letter-spacing: 0; }
      `}</style>

      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.material_requisitions',
        documentId: d.id,
        filename: `Вимога ${d.document_number}`,
        orgName: org.name,
      }} />

      <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
        <div style={{ fontSize: '10pt' }}>{org.full_name || org.name}</div>
        {partyCodesText(org) && <div style={{ fontSize: '10pt', color: '#555' }}>{partyCodesText(org)}</div>}
        <div style={{ fontSize: '8pt', color: '#555', marginTop: 2 }}>
          Типова форма № М-11 (за мотивами; наказ Мінстату України від 21.06.1996 № 193)
        </div>
      </div>

      <h2 style={{ textAlign: 'center', margin: '0.75rem 0' }}>
        Накладна-вимога на відпуск матеріалів № {d.document_number}
        {' '}від {new Date(d.document_date).toLocaleDateString('uk-UA')}
      </h2>

      <table style={{ width: '100%', fontSize: '10pt', marginBottom: '0.5rem' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%' }}>
              <span style={{ color: '#555' }}>Відправник (склад): </span>
              {!multiWarehouse
                ? (whName.get(d.source_warehouse_id ?? '') ?? '—')
                : 'за рядками (див. таблицю)'}
            </td>
            <td>
              <span style={{ color: '#555' }}>Одержувач (підрозділ): </span>
              {dept?.name ?? 'виробництво'}
            </td>
          </tr>
        </tbody>
      </table>

      <table className="mr-table">
        <thead>
          <tr>
            <th style={{ width: 30 }}>№</th>
            <th>Матеріальні цінності (найменування)</th>
            {multiWarehouse && <th style={{ width: 130 }}>Склад</th>}
            <th style={{ width: 45 }}>Од.</th>
            <th style={{ width: 80 }}>Відпущено, к-сть</th>
            {d.posted && <th style={{ width: 80 }}>Ціна</th>}
            {d.posted && <th style={{ width: 90 }}>Сума</th>}
          </tr>
        </thead>
        <tbody>
          {d.items.map((it, i) => {
            const nom = nomById.get(it.nomenclature_id);
            return (
              <tr key={i}>
                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                <td>{nom?.name ?? '—'}{nom?.code && <span style={{ color: '#555' }}> ({nom.code})</span>}</td>
                {multiWarehouse && <td>{whName.get(effWh(it)) ?? '—'}</td>}
                <td style={{ textAlign: 'center' }}>{uomOf(it)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(it.quantity)}</td>
                {d.posted && (
                  <td style={{ textAlign: 'right' }}>{fmtMoney(it.cost_per_unit ?? 0)}</td>
                )}
                {d.posted && (
                  <td style={{ textAlign: 'right' }}>{fmtMoney(it.total_cost ?? 0)}</td>
                )}
              </tr>
            );
          })}
        </tbody>
        {d.posted && (
          <tfoot>
            <tr style={{ fontWeight: 700 }}>
              <td colSpan={multiWarehouse ? 5 : 4} style={{ textAlign: 'right' }}>Разом:</td>
              <td></td>
              <td style={{ textAlign: 'right' }}>{fmtMoney(totalCost)}</td>
            </tr>
          </tfoot>
        )}
      </table>

      <p style={{ margin: '0.5rem 0' }}>
        Всього найменувань {d.items.length}
        {d.posted && <>, на суму <strong>{fmtMoney(totalCost)} грн</strong></>}.
      </p>
      {d.comment && <p style={{ margin: '0.5rem 0', color: '#333' }}>Коментар: {d.comment}</p>}

      <table style={{ width: '100%', marginTop: 28, fontSize: '10.5pt' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Відпустив (МВО складу)</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б.</span>
            </td>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Отримав (підрозділ)</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б.</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
